import type Hls from 'hls.js';

export interface HlsSupport {
  /** canPlayType claims native HLS. UNTRUSTWORTHY on its own: desktop Chrome answers
   *  "maybe" yet cannot play HLS without MSE — only use native when `mse` is false
   *  (iPhone Safari, where MediaSource doesn't exist). */
  native: boolean;
  /** MediaSource browsers (Chrome/Firefox/Edge/Android/desktop Safari): hls.js attaches. */
  mse: boolean;
}

/**
 * Client-only HLS capability probe. Call after mount (SSR renders the MP4 fallback so
 * hydration stays consistent).
 */
export const getHlsSupport = (): HlsSupport => {
  if (typeof window === 'undefined') return { native: false, mse: false };

  const probe = document.createElement('video');
  return {
    native: Boolean(probe.canPlayType('application/vnd.apple.mpegurl')),
    mse: 'MediaSource' in window,
  };
};

interface AttachHlsOptions {
  /** Start playback as soon as the manifest is parsed (autoplay through hls.js is async). */
  autoplay?: boolean;
  /** Progressive MP4 to play when HLS fails fatally (e.g. CDN CORS misconfig blocks the
   *  manifest XHR). Without it a fatal error just tears the stream down. */
  fallbackUrl?: string;
}

/**
 * Attaches an HLS manifest to a <video> through hls.js (dynamically imported so the
 * ~60KB library only loads on browsers/pages that need it). Native-HLS browsers should
 * NOT go through here — give them the manifest URL as plain `src` instead.
 *
 * Returns a cleanup that destroys the hls.js instance and detaches the media source.
 */
export const attachHls = (video: HTMLVideoElement, manifestUrl: string, options: AttachHlsOptions = {}): (() => void) => {
  let destroyed = false;
  let hls: Hls | null = null;

  void import('hls.js').then(({ default: HlsLib }) => {
    if (destroyed || !HlsLib.isSupported()) return;

    hls = new HlsLib({
      // Stories are short vertical clips — a modest forward buffer keeps memory and
      // bandwidth low while still absorbing network jitter.
      maxBufferLength: 15,
      capLevelToPlayerSize: true,
    });
    hls.loadSource(manifestUrl);
    hls.attachMedia(video);

    if (options.autoplay) {
      hls.on(HlsLib.Events.MANIFEST_PARSED, () => {
        if (!destroyed) void video.play().catch(() => {});
      });
    }

    // Give up on HLS and play the progressive MP4 instead. Deliberate for network-fatal
    // errors: they include CORS blocks on the manifest XHR (curl/Safari work, MSE
    // browsers fail), which no amount of hls.js retrying heals.
    const fallbackToMp4 = () => {
      hls?.destroy();
      hls = null;
      if (destroyed || !options.fallbackUrl) return;
      video.src = options.fallbackUrl;
      if (options.autoplay) void video.play().catch(() => {});
    };

    let mediaRecoveryAttempted = false;
    hls.on(HlsLib.Events.ERROR, (_event, data) => {
      if (!hls || !data.fatal) return;
      // MEDIA errors get hls.js's one-shot recovery; everything else (network incl. CORS,
      // mux/other) drops straight to the MP4 fallback.
      if (data.type === HlsLib.ErrorTypes.MEDIA_ERROR && !mediaRecoveryAttempted) {
        mediaRecoveryAttempted = true;
        hls.recoverMediaError();
        return;
      }
      fallbackToMp4();
    });
  });

  return () => {
    destroyed = true;
    hls?.destroy();
    hls = null;
  };
};

/** The 'hls' entry of a video's streams array, if any. */
export const findHlsStreamUrl = (streams?: { type?: string; url?: string }[] | null): string | undefined => (
  streams?.find((s) => s?.type === 'hls')?.url ?? undefined
);
